// dependencies.
import { useEffect, useMemo, useState } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import styled from 'styled-components'
// components.
import AdminTemplate from '../../templates/AdminPanel'
import Header from '../../components/Layout/Header'
import { Button } from '../../components/Button'
import { Form } from '../../components/Form'
import { Body } from '../../components/Typography'
// utils.
import notifyToast from '../../js/notifyToast'
import { auth, db, signOut } from '../../js/firebase/firebase'
// constants.
import { SETTINGS_FORM, SETTINGS_NOTIFICATION_RECIPIENTS_FORM } from './constants'
import { useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../js/store/hooks'
import { logout, setCurrentUserData } from '../../js/auth/authSlice'
import { selectUserData } from '../../js/auth/authSelectors'

/********************************/
/*                              */
/*    Admins Settings Screen    */
/*                              */
/********************************/

// Partials:
const StyledForm = styled(Form)({
  padding: '40px 16px',
})

const parseRecipients = (value = '') =>
  `${value}`
    .split(/[\n,;]+/g)
    .map((email) => email.trim())
    .filter(Boolean)

// Main component:
const AdminsSettingsScreen = ({
  navMenu,
  navTitle,
  items = SETTINGS_FORM,
  mode = 'account',
}) => {
  // contexts.
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const userData = useAppSelector(selectUserData)
  const canManageNotificationSettings = userData?.access === 'developer'
  const isSystemSettingsMode = mode === 'system'
  const shouldShowAccountSettings = !isSystemSettingsMode
  const shouldShowNotificationSettings = isSystemSettingsMode && canManageNotificationSettings
  const [notificationRecipients, setNotificationRecipients] = useState({
    TS: '',
    TO: '',
    TM: '',
    TQ: '',
  })

  useEffect(() => {
    if (!isSystemSettingsMode) return
    if (!canManageNotificationSettings) {
      notifyToast('Solo developer puede acceder a ajustes del sistema', 'warn')
      navigate('/admin/ajustes')
      return
    }
    const loadNotificationSettings = async () => {
      try {
        const settingsRef = doc(db, 'notificationSettings', 'cardCreationRecipients')
        const settingsSnap = await getDoc(settingsRef)
        if (!settingsSnap.exists()) return
        const data = settingsSnap.data()
        setNotificationRecipients({
          TS: Array.isArray(data?.TS) ? data.TS.join('\n') : '',
          TO: Array.isArray(data?.TO) ? data.TO.join('\n') : '',
          TM: Array.isArray(data?.TM) ? data.TM.join('\n') : '',
          TQ: Array.isArray(data?.TQ) ? data.TQ.join('\n') : '',
        })
      } catch (error) {
        console.error('Error loading notification settings:', error)
        notifyToast('No se pudo cargar la configuracion de notificaciones', 'error')
      }
    }
    loadNotificationSettings()
  }, [canManageNotificationSettings, isSystemSettingsMode, navigate])

  const settingsItems = useMemo(
    () =>
      shouldShowNotificationSettings
        ? SETTINGS_NOTIFICATION_RECIPIENTS_FORM
        : shouldShowAccountSettings
          ? items
          : [],
    [items, shouldShowAccountSettings, shouldShowNotificationSettings]
  )
  const settingsFormKey = useMemo(
    () =>
      `${userData?.uid || 'user'}-${notificationRecipients.TS}-${notificationRecipients.TO}-${notificationRecipients.TM}-${notificationRecipients.TQ}`,
    [notificationRecipients.TQ, notificationRecipients.TM, notificationRecipients.TO, notificationRecipients.TS, userData?.uid]
  )
  // ------------------------------------------------------------------------------------------- //
  // UPDATE DATABASE DATA                                                                        //
  // ------------------------------------------------------------------------------------------- //

  // handle user information update.
  const handleUpdateUserClick = async (obj) => {
    const userRef = doc(db, 'users', userData.uid)
    const updateFields = {
      firstname: obj.firstname,
      lastname: obj.lastname,
      updatedDate: new Date(),
    }
    await (shouldShowAccountSettings
      ? setDoc(userRef, updateFields, { merge: true })
      : Promise.resolve())
      .then(async () => {
        if (shouldShowNotificationSettings) {
          const settingsRef = doc(db, 'notificationSettings', 'cardCreationRecipients')
          await setDoc(
            settingsRef,
            {
              TS: parseRecipients(obj.notificationRecipientsTS),
              TO: parseRecipients(obj.notificationRecipientsTO),
              TM: parseRecipients(obj.notificationRecipientsTM),
              TQ: parseRecipients(obj.notificationRecipientsTQ),
              enabled: true,
              updatedAt: new Date(),
              updatedBy: userRef,
            },
            { merge: true }
          )
        }
        if (shouldShowAccountSettings) {
          dispatch(setCurrentUserData({ userData: { ...userData, ...updateFields } }))
        }
        notifyToast('Datos guardados correctamente', 'success')
      })
      .catch(() => notifyToast('Ha ocurrido un error', 'error'))
  }

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        dispatch(logout())
        navigate('/signin')
      })
      .catch((error) => error.message)
  }
  // ------------------------------------------------------------------------------------------- //
  // CONTENT                                                                                     //
  // ------------------------------------------------------------------------------------------- //

  // only show if there's items.
  if (!items) return null

  // return content.
  return (
    <AdminTemplate navigation={navMenu} sectionTitle={navTitle}>
      <Header>
        <Button onClick={() => handleLogout()}>Cerrar sesión</Button>
      </Header>

      <StyledForm
        key={settingsFormKey}
        height="calc(100vh - 120px)"
        items={settingsItems}
        defaultValues={{
          firstname: shouldShowAccountSettings ? userData.firstname : '',
          lastname: shouldShowAccountSettings ? userData.lastname : '',
          document: shouldShowAccountSettings ? userData.document : '',
          email:
            shouldShowAccountSettings && userData.email !== `${userData.document}@ccu.uy`
              ? userData.email
              : '',
          notificationRecipientsTS: notificationRecipients.TS,
          notificationRecipientsTO: notificationRecipients.TO,
          notificationRecipientsTM: notificationRecipients.TM,
          notificationRecipientsTQ: notificationRecipients.TQ,
        }}
        submitLabel={shouldShowNotificationSettings ? 'Guardar configuración' : 'Actualizar'}
        onClick={(obj) => handleUpdateUserClick(obj)}
      />
      {shouldShowNotificationSettings ? (
        <Body align="center" color="grey" type="caption">
          Solo developer puede editar esta configuración.
        </Body>
      ) : null}
    </AdminTemplate>
  )
}

export default AdminsSettingsScreen
